import type {
  AudienceBuilderDisplaySegment,
  AudienceBuilderPatient,
  AudienceBuilderPhase,
  AudienceBuilderSegmentDef,
  AudienceBuilderSegmentState,
  AudienceBuilderTone,
} from './audienceBuilderTypes';

export const AB_TONE: Record<AudienceBuilderTone, { a: string; t: string }> = {
  "info": {
    "a": "var(--info)",
    "t": "var(--info-tint)"
  },
  "ai": {
    "a": "var(--ai-3)",
    "t": "var(--ai-tint)"
  },
  "gold": {
    "a": "var(--gold)",
    "t": "var(--gold-tint)"
  },
  "teal": {
    "a": "var(--ai-5)",
    "t": "rgba(63,184,168,0.14)"
  },
  "lav": {
    "a": "#7A5CFF",
    "t": "rgba(122,79,214,0.13)"
  },
  "terra": {
    "a": "var(--terracotta)",
    "t": "var(--terracotta-tint)"
  }
};

export const AB_SEGMENTS: Record<string, AudienceBuilderSegmentDef> = {
  "standard": {
    "id": "standard",
    "label": "Standard hygiene",
    "interval": "6 months",
    "tone": "info",
    "rule": "No special clinical conditions",
    "isDefault": true,
    "sqlExplanation": "All active patients with no overriding segment match"
  },
  "implant": {
    "id": "implant",
    "label": "Implant patients",
    "interval": "4 months",
    "tone": "ai",
    "rule": "Implant codes (D6010–D6067) in history",
    "insuranceNote": "3rd cleaning usually not covered by insurance",
    "sqlExplanation": "Patients with CDT codes D6010–D6199 ever performed",
    "suggested": true
  },
  "perio": {
    "id": "perio",
    "label": "Perio maintenance",
    "interval": "3 months",
    "tone": "gold",
    "rule": "Perio codes (D4341/D4342) in last 24 months",
    "sqlExplanation": "Patients with D4341 or D4342 in the past 24 months",
    "suggested": true
  },
  "high-caries": {
    "id": "high-caries",
    "label": "High caries risk",
    "interval": "4 months",
    "tone": "teal",
    "rule": "3+ caries restorations in past 2 years",
    "sqlExplanation": "Patients with 3+ restorations in the past 24 months",
    "suggested": true
  },
  "diabetic": {
    "id": "diabetic",
    "label": "Diabetic patients",
    "interval": "4 months",
    "tone": "lav",
    "rule": "Active medical alert: diabetes",
    "sqlExplanation": "Patients with an active diabetes medical alert"
  },
  "new-patients": {
    "id": "new-patients",
    "label": "New patients welcome",
    "interval": "Event-triggered",
    "tone": "info",
    "rule": "First visit in past 7 days",
    "sqlExplanation": "first_visit_date within the past 7 days — triggers on patient creation"
  }
};

const AB_PREVIEW_FIRST_NAMES = [
  'Emma', 'Noah', 'Olivia', 'Liam', 'Ava', 'Ethan', 'Sophia', 'Mason', 'Isabella', 'Lucas',
  'Mia', 'Henry', 'Charlotte', 'Alexander', 'Amelia', 'Benjamin', 'Harper', 'Daniel', 'Evelyn', 'Matthew',
  'Abigail', 'Joseph', 'Emily', 'Samuel', 'Elizabeth', 'Jack', 'Sofia', 'Owen', 'Avery', 'Sebastian',
] as const;

const AB_PREVIEW_LAST_NAMES = [
  'Wilson', 'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Garcia', 'Martinez',
  'Robinson', 'Clark', 'Rodriguez', 'Lewis', 'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'King',
] as const;

const AB_PREVIEW_SEGMENTS = ['standard', 'implant', 'perio', 'high-caries', 'diabetic'] as const;

const AB_PREVIEW_TAGS: Record<(typeof AB_PREVIEW_SEGMENTS)[number], string[]> = {
  standard: ['Regular'],
  implant: ['Implant #14'],
  perio: ['Perio maint.'],
  'high-caries': ['High caries'],
  diabetic: ['Diabetic'],
};

function buildAudiencePreviewPatients(): AudienceBuilderPatient[] {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return AB_PREVIEW_FIRST_NAMES.map((first, index) => {
    const last = AB_PREVIEW_LAST_NAMES[index % AB_PREVIEW_LAST_NAMES.length]!;
    const segment = AB_PREVIEW_SEGMENTS[index % AB_PREVIEW_SEGMENTS.length]!;
    const visitMonth = months[index % 12]!;
    const dueMonth = months[(index + 4) % 12]!;
    const day = String((index % 27) + 1).padStart(2, '0');
    const dueDay = String(((index + 11) % 27) + 1).padStart(2, '0');
    return {
      name: `${first} ${last}`,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@email.com`,
      lastVisit: `${visitMonth} ${day}, 2025`,
      segment,
      tags: AB_PREVIEW_TAGS[segment],
      dueDate: `${dueMonth} ${dueDay}, 2026`,
    };
  });
}

export const AB_PATIENTS: AudienceBuilderPatient[] = [
  {
    "name": "James Harrison",
    "email": "james.h@email.com",
    "lastVisit": "Sep 14, 2025",
    "segment": "implant",
    "tags": [
      "Implant #14",
      "Implant #19"
    ],
    "dueDate": "Jan 14, 2026"
  },
  {
    "name": "Maria Gonzalez",
    "email": "maria.g@email.com",
    "lastVisit": "Oct 2, 2025",
    "segment": "perio",
    "tags": [
      "Perio maint."
    ],
    "dueDate": "Jan 2, 2026"
  },
  {
    "name": "Robert Chen",
    "email": "robert.c@email.com",
    "lastVisit": "Nov 18, 2025",
    "segment": "standard",
    "tags": [
      "Regular"
    ],
    "dueDate": "May 18, 2026"
  },
  {
    "name": "Linda Park",
    "email": "linda.p@email.com",
    "lastVisit": "Aug 30, 2025",
    "segment": "implant",
    "tags": [
      "Implant #8"
    ],
    "dueDate": "Dec 30, 2025"
  },
  {
    "name": "Tom Baker",
    "email": "tom.b@email.com",
    "lastVisit": "Dec 5, 2025",
    "segment": "standard",
    "tags": [
      "VIP"
    ],
    "dueDate": "Jun 5, 2026"
  },
  {
    "name": "Sarah Kim",
    "email": "sarah.k@email.com",
    "lastVisit": "Jul 22, 2025",
    "segment": "perio",
    "tags": [
      "Perio maint."
    ],
    "dueDate": "Oct 22, 2025"
  },
  {
    "name": "David Wright",
    "email": "david.w@email.com",
    "lastVisit": "Oct 28, 2025",
    "segment": "high-caries",
    "tags": [
      "High caries"
    ],
    "dueDate": "Feb 28, 2026"
  },
  {
    "name": "Amy Foster",
    "email": "amy.f@email.com",
    "lastVisit": "Sep 5, 2025",
    "segment": "implant",
    "tags": [
      "Implant #30"
    ],
    "dueDate": "Jan 5, 2026"
  },
  {
    "name": "Kevin Nguyen",
    "email": "kevin.n@email.com",
    "lastVisit": "Nov 1, 2025",
    "segment": "standard",
    "tags": [
      "Recent"
    ],
    "dueDate": "May 1, 2026"
  },
  {
    "name": "Jennifer Lee",
    "email": "jennifer.l@email.com",
    "lastVisit": "Aug 12, 2025",
    "segment": "perio",
    "tags": [
      "Perio",
      "Diabetic"
    ],
    "dueDate": "Nov 12, 2025"
  },
  {
    "name": "Mark Davis",
    "email": "mark.d@email.com",
    "lastVisit": "Oct 15, 2025",
    "segment": "standard",
    "tags": [
      "Regular"
    ],
    "dueDate": "Apr 15, 2026"
  },
  {
    "name": "Rachel Torres",
    "email": "rachel.t@email.com",
    "lastVisit": "Sep 20, 2025",
    "segment": "high-caries",
    "tags": [
      "High caries"
    ],
    "dueDate": "Jan 20, 2026"
  },
  ...buildAudiencePreviewPatients(),
];

export const AB_COUNTS: Record<string, number> = {
  "standard": 347,
  "implant": 56,
  "perio": 84,
  "high-caries": 42,
  "diabetic": 31,
  "new-patients": 0
};

export const AB_PHASE_SEGMENTS: Record<AudienceBuilderPhase, string[]> = {
  "entry-new-custom": [],
  "entry-library": [
    "standard",
    "implant",
    "perio",
    "high-caries"
  ],
  "entry-editing": [
    "standard",
    "implant",
    "perio",
    "diabetic"
  ],
  "entry-library-has-failure": [
    "standard",
    "implant",
    "perio",
    "high-caries"
  ],
  "custom-metadata": [],
  "chat": [
    "standard",
    "implant",
    "perio",
    "diabetic"
  ],
  "clarification": [
    "standard",
    "implant"
  ],
  "out-of-scope": [
    "standard",
    "implant",
    "perio"
  ],
  "summary": [
    "standard",
    "implant",
    "perio",
    "high-caries"
  ],
  "confirmed": [
    "standard",
    "implant",
    "perio",
    "high-caries"
  ]
};

export function abDisplaySegments(
  phase: AudienceBuilderPhase,
  segmentState: AudienceBuilderSegmentState,
): AudienceBuilderDisplaySegment[] {
  const ids = AB_PHASE_SEGMENTS[phase] ?? [];
  let segs: AudienceBuilderDisplaySegment[] = ids.map((id) => {
    let resolution: AudienceBuilderDisplaySegment['resolution'] = 'resolved';
    let patients: number | null = AB_COUNTS[id] ?? 0;
    let warnings: string[] = [];
    let failureReason: string | null = null;
    let note: string | null = null;
    const base = AB_SEGMENTS[id]!;

    if (phase === 'entry-library-has-failure' && id === 'perio') {
      resolution = 'resolution_failed';
      patients = null;
      failureReason =
        "References a perio maintenance condition that isn't configured in your practice yet.";
    } else if (segmentState === 'all-resolving') {
      resolution = 'resolving';
      patients = null;
    } else if (segmentState === 'show-amber' && id === 'implant') {
      resolution = 'resolved_with_warning';
      patients = 0;
      warnings = [
        'This practice has 2,400 patients but no implant history matched. Double-check the description.',
      ];
    } else if (segmentState === 'show-red' && id === 'perio') {
      resolution = 'resolution_failed';
      patients = null;
      failureReason = "Couldn't match the description to a known pattern.";
    } else if (segmentState === 'mixed' && id === 'high-caries') {
      resolution = 'resolving';
      patients = null;
    }
    return { ...base, resolution, patients, warnings, failureReason, note };
  });

  if (segmentState === 'show-blue' && phase !== 'entry-new-custom' && phase !== 'custom-metadata') {
    segs = segs.filter((s) => s.id !== 'diabetic');
    const np = AB_SEGMENTS['new-patients']!;
    segs.push({
      ...np,
      resolution: 'resolved_event_triggered',
      patients: 0,
      warnings: [],
      failureReason: null,
      note: "This recall enrolls patients when the trigger fires. Zero today is expected — it's forward-looking.",
    });
  }
  return segs;
}

export function audienceBuilderPhaseForMode(
  mode: 'new' | 'library' | 'edit',
  enabled?: boolean,
): AudienceBuilderPhase {
  if (mode === 'new') return 'summary';
  if (mode === 'library') return 'entry-library';
  return enabled ? 'entry-editing' : 'entry-library';
}
